import React, {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  ReactNode,
  ElementType,
} from "react";

import classNames from "classnames";

import {
  StyledCommandPaletteItem,
  StyledCommandPaletteItemLoader,
  StyledIcon,
} from "./CommandPalette.styles";
import { useSelectedContext } from "./context";
import { useSearchContext } from "./context/SearchContext";
import { findHighlightedSearch } from "../../utils/findHighlightedSearch";
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from "../../utils/types";
import { SkeletonLoader } from "../Loaders";
import { Typography } from "../Typography/Typography";

const generateRandomWidth = () => {
  // generate range 30-80
  const randomNum = Math.round(Math.random() * 30 + 30);

  return randomNum + "%";
};

export interface CommandPaletteItemProps extends ComponentPropsWithoutRef<"a"> {
  title?: string;
  renderIcon?: () => ReactNode;
  className?: string;
  index?: number;
  selected?: number;
  loading?: boolean;
}

export type PolymorphicCommandPaletteItemProps<C extends ElementType> =
  PolymorphicComponentPropsWithRef<C, CommandPaletteItemProps>;

export type CommandPaletteComponent = <
  C extends ElementType = typeof CommandPaletteItem
>(
  props: PolymorphicCommandPaletteItemProps<C>
) => ReactElement | ReactNode | null;

export const CommandPaletteItem: CommandPaletteComponent & {
  displayName?: string;
} = forwardRef(
  <C extends ElementType = typeof CommandPaletteItem>(
    {
      title,
      renderIcon,
      className,
      index,
      loading,
      as,
      ...rest
    }: PolymorphicCommandPaletteItemProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || "a";
    const icon = renderIcon && renderIcon();
    const { selected, setSelected } = useSelectedContext();
    const { search } = useSearchContext();

    const onMouseEnter = () => {
      setSelected(index as number);
    };

    if (loading) {
      return (
        <StyledCommandPaletteItemLoader aria-busy={true} aria-disabled={true}>
          <SkeletonLoader
            css={{ width: 24, height: 24, borderRadius: "100%" }}
          />
          <SkeletonLoader css={{ width: generateRandomWidth(), height: 16 }} />
        </StyledCommandPaletteItemLoader>
      );
    }

    return (
      <li>
        <StyledCommandPaletteItem
          {...rest}
          as={Component}
          ref={ref}
          className={classNames("cp-item", className)}
          tabIndex={1}
          aria-selected={selected === index}
          onMouseEnter={onMouseEnter}
          $selected={index === selected}
        >
          <StyledIcon>{icon}</StyledIcon>
          {search && title ? (
            <Typography
              overflow="truncate"
              whitespace="no-wrap"
              variant="Body 1"
              color="primary"
              dangerouslySetInnerHTML={{
                __html: findHighlightedSearch(search, title),
              }}
            />
          ) : null}
        </StyledCommandPaletteItem>
      </li>
    );
  }
);

CommandPaletteItem.displayName = "CommandPaletteItem";
